import styled from 'styled-components';
import { changeColorAlpha } from '@elfsight/lib-utils';
import { getBrightInvertedColor } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import {
  formatWeekday,
  getClassName,
  getTemperatureUnit,
  type TemperatureUnit,
  useTemperature
} from '../../helpers';
import type { ForecastDailyListItem } from '../api/request';
import { Icon } from '../common/icon';

type DailyItemProps = {
  id: number;
  data: ForecastDailyListItem;
  timezone: number;
};

export const DailyItem = ({ data, timezone }: DailyItemProps) => {
  const { dailyForecastBackgroundColor, language } = useSettings();
  const { language: locale } = language ?? {};
  const units = getTemperatureUnit(locale);

  const {
    dt,
    temp: { min, max },
    weather: [currentWeather]
  } = data;

  const { icon } = currentWeather;
  const rawDate = new Date(dt * 1000);
  const shiftedDate = new Date((dt + timezone) * 1000);

  const date = new Intl.DateTimeFormat(locale, {
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC'
  }).format(shiftedDate);

  const weekday = formatWeekday({
    date: rawDate,
    locale,
    timezone
  });

  const { processedTemperature: processedTemperatureMin } = useTemperature({
    temp: min
  });
  const { processedTemperature: processedTemperatureMax } = useTemperature({
    temp: max
  });

  return (
    <Component _dailyForecastBackgroundColor={dailyForecastBackgroundColor}>
      <div>
        <DateBlock _dailyForecastBackgroundColor={dailyForecastBackgroundColor}>
          {date}
        </DateBlock>

        <DayBlock _dailyForecastBackgroundColor={dailyForecastBackgroundColor}>
          {weekday}
        </DayBlock>
      </div>

      <IconBlock>
        <Icon name={icon} />
      </IconBlock>

      <Temp _dailyForecastBackgroundColor={dailyForecastBackgroundColor}>
        <Max
          _dailyForecastBackgroundColor={dailyForecastBackgroundColor}
          _units={units}
        >
          {processedTemperatureMax}
        </Max>

        <Min
          _dailyForecastBackgroundColor={dailyForecastBackgroundColor}
          _units={units}
        >
          {processedTemperatureMin}
        </Min>
      </Temp>
    </Component>
  );
};

const Component = styled.div.attrs<{
  _dailyForecastBackgroundColor: string | null;
}>({
  className: getClassName('daily-item', 'component'),
  tabIndex: 0
})`
  display: flex;
  padding: 8px 24px;
  align-items: center;
  transition: background-color 0.3s ease;
  background-color: ${({ _dailyForecastBackgroundColor }) =>
    _dailyForecastBackgroundColor};
`;

const DateBlock = styled.div.attrs<{
  _dailyForecastBackgroundColor: string | null;
}>({
  className: getClassName('daily-item', 'date')
})`
  font-size: 12px;
  line-height: 1.39;
  opacity: 0.5;

  ${({ _dailyForecastBackgroundColor }) =>
    _dailyForecastBackgroundColor
      ? `color: ${getBrightInvertedColor(_dailyForecastBackgroundColor, [
          'rgb(255, 255, 255)',
          'rgb(29, 33, 41)'
        ])};`
      : ''}
`;

const DayBlock = styled.div.attrs<{
  _dailyForecastBackgroundColor: string | null;
}>({
  className: getClassName('daily-item', 'day')
})`
  font-size: 15px;
  line-height: 1.39;
  text-transform: capitalize;

  color: ${({ _dailyForecastBackgroundColor }) =>
    _dailyForecastBackgroundColor
      ? getBrightInvertedColor(_dailyForecastBackgroundColor, [
          'rgb(255, 255, 255)',
          'rgb(29, 33, 41)'
        ])
      : '#111'};
`;

const Temp = styled.div.attrs<{
  _dailyForecastBackgroundColor: string | null;
}>({
  className: getClassName('daily-item', 'temp')
})`
  display: flex;
  align-items: center;

  ${({ _dailyForecastBackgroundColor }) =>
    _dailyForecastBackgroundColor
      ? `color: ${getBrightInvertedColor(_dailyForecastBackgroundColor, [
          'rgb(255, 255, 255)',
          'rgb(29, 33, 41)'
        ])};`
      : ''}
`;

const IconBlock = styled.div.attrs({
  className: getClassName('daily-item', 'icon')
})`
  width: 40px;
  height: 40px;
  margin-left: auto;
`;

const Min = styled.div.attrs<{
  _dailyForecastBackgroundColor: string | null;
  _units: TemperatureUnit;
}>({
  className: getClassName('daily-item', 'min')
})`
  ${({ _dailyForecastBackgroundColor, _units }) => `
    font-size: 14px;
    margin-left: 8px;
    text-align: center;
    line-height: 1.39;
    min-width: 53px;
    ${_units === 'metric' ? 'min-width: 34px;' : ''}
    ${_units === 'imperial' ? 'min-width: 38px;' : ''}

    color: ${
      _dailyForecastBackgroundColor
        ? changeColorAlpha(
            getBrightInvertedColor(_dailyForecastBackgroundColor, [
              'rgb(255, 255, 255)',
              'rgb(29, 33, 41)'
            ]),
            -0.5
          )
        : 'rgba(17, 17, 17, 0.5)'
    };
  `}
`;

const Max = styled.div.attrs<{
  _dailyForecastBackgroundColor: string | null;
  _units: TemperatureUnit;
}>({
  className: getClassName('daily-item', 'max')
})`
  ${({ _dailyForecastBackgroundColor, _units }) => `
    font-size: 14px;
    margin-left: 8px;
    text-align: center;
    line-height: 1.39;
    min-width: ${_units === 'metric' ? 34 : 38}px;

    color: ${
      _dailyForecastBackgroundColor
        ? getBrightInvertedColor(_dailyForecastBackgroundColor, [
            'rgb(255, 255, 255)',
            'rgb(29, 33, 41)'
          ])
        : '#111'
    };
  `}
`;
